import React, { useEffect, useState } from 'react'
import { useParams, useLocation, useNavigate, Outlet } from 'react-router-dom'
import { Card, CardHeader, CardTitle } from '../ui/card'
import { Button } from '../ui/button'
import { Badge } from '../ui/badge'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../ui/breadcrumb'
import {
  Loader2,
  Settings,
  Calendar,
  Users,
  Map,
  Award,
  CreditCard,
  ClipboardList,
  Copy,
  LayoutGrid,
  Wand2,
} from 'lucide-react'
import { useApplicationStore } from '../../stores/useApplicationStore'
import EventEndpoints from '../../endpoints/EventEndpoints'
import { DateTime } from 'luxon'
import { cn } from '@/lib/utils'
import { DuplicateEventDialog } from './DuplicateEventDialog'
import { EventProvider, useEventContext } from './EventContext'
import { useSessionStore } from '../../stores/useSessionStore'
import { useEventSetupStatus } from './setup/useEventSetupStatus'
import { wizardHref } from './setup/eventSetupStatus'
import { EventSetupChecklist } from './setup/EventSetupChecklist'

const configTabs = [
  {
    id: 'info',
    label: 'Basic Info',
    path: '',
    icon: Settings,
  },
  {
    id: 'badges',
    label: 'Badge Design',
    path: 'badges',
    icon: CreditCard,
  },
  {
    id: 'accreditation-levels',
    label: 'Accreditation Levels',
    path: 'accreditation-levels',
    icon: Award,
  },
  {
    id: 'roles',
    label: 'Roles',
    path: 'roles',
    icon: Users,
  },
  {
    id: 'schedule-config',
    label: 'Schedule Config',
    path: 'schedule-config',
    icon: Map,
  },
  {
    id: 'generic-identities',
    label: 'Generic Identities',
    path: 'generic-identities',
    icon: LayoutGrid,
  },
]

interface EventConfigurationModernProps {
  children?: React.ReactNode
}

export const EventConfigurationModern: React.FC<
  EventConfigurationModernProps
> = ({ children }) => {
  return (
    <EventProvider>
      <EventConfigurationInner>{children}</EventConfigurationInner>
    </EventProvider>
  )
}

const EventConfigurationInner: React.FC<{ children?: React.ReactNode }> = ({
  children,
}) => {
  const { eventId } = useParams<{ eventId: string }>()
  const location = useLocation()
  const navigate = useNavigate()

  const { event, loading, setEvent, canEdit } = useEventContext()
  const [duplicateOpen, setDuplicateOpen] = useState(false)
  const { hasAdmin, hasIdentityManagerFor } = useSessionStore()

  // Drives both the checklist and whether the wizard button is worth showing.
  const { status: setupStatus } = useEventSetupStatus(event, {
    includeStaff: true,
  })
  const canProvision = !!(
    hasAdmin() ||
    (event?.client && hasIdentityManagerFor(event.client.id))
  )

  const getCurrentTab = () => {
    const pathSegments = location.pathname.split('/')
    const configIndex = pathSegments.findIndex(
      (segment) => segment === 'config'
    )
    const tabPath = pathSegments[configIndex + 1] || ''
    const tab = configTabs.find((t) => t.path === tabPath)
    return tab?.id || 'info'
  }

  const handleTabChange = (tabId: string) => {
    const tab = configTabs.find((t) => t.id === tabId)
    if (tab && eventId) {
      const newPath = `/event/${eventId}/config${tab.path ? '/' + tab.path : ''}`
      navigate(newPath)
    }
  }

  const formatDate = (date: Date | string | number | undefined) => {
    if (!date) return '-'
    try {
      let dateObj: DateTime
      if (typeof date === 'number') {
        dateObj = DateTime.fromMillis(date)
      } else if (typeof date === 'string') {
        dateObj = DateTime.fromISO(date)
      } else {
        dateObj = DateTime.fromJSDate(date)
      }
      return dateObj.isValid ? dateObj.toFormat('MMM dd, yyyy') : '-'
    } catch {
      return '-'
    }
  }

  if (loading && !event) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-muted-foreground" />
            <p className="mt-2 text-sm text-muted-foreground">
              Loading event...
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (!event) {
    return (
      <div className="container mx-auto px-4 py-6">
        <div className="text-center">
          <p className="text-muted-foreground">Event not found</p>
        </div>
      </div>
    )
  }

  const currentTab = getCurrentTab()

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Breadcrumb Navigation */}
      <Breadcrumb>
        <BreadcrumbList>
          <BreadcrumbItem>
            <BreadcrumbLink href="/event">Events</BreadcrumbLink>
          </BreadcrumbItem>
          <BreadcrumbSeparator />
          <BreadcrumbItem>
            <BreadcrumbPage className="font-semibold">
              {event.name}
            </BreadcrumbPage>
          </BreadcrumbItem>
        </BreadcrumbList>
      </Breadcrumb>

      {/* Event Header */}
      <Card>
        <CardHeader className="pb-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10">
                <Calendar className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-2xl">{event.name}</CardTitle>
                <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                  <span>
                    {formatDate(event.startDate)} - {formatDate(event.endDate)}
                  </span>
                  <Badge variant="secondary">{event.client?.name}</Badge>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              {canEdit && (
                <Button
                  onClick={() =>
                    navigate(
                      wizardHref(
                        eventId!,
                        setupStatus.firstIncompleteStepId ?? undefined
                      )
                    )
                  }
                  title="Walk through everything needed before building a schedule"
                >
                  <Wand2 className="mr-2 h-4 w-4" />
                  Setup Wizard
                </Button>
              )}
              <Button
                variant="outline"
                onClick={() => navigate(`/event/${eventId}/provision`)}
              >
                <ClipboardList className="mr-2 h-4 w-4" />
                Provision
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(`/event/${eventId}/schedule`)}
              >
                <Calendar className="mr-2 h-4 w-4" />
                Schedule
              </Button>
              <Button
                variant="outline"
                onClick={() => setDuplicateOpen(true)}
                disabled={!canEdit}
                title={
                  !canEdit
                    ? 'You do not have permission to duplicate this event'
                    : undefined
                }
              >
                <Copy className="mr-2 h-4 w-4" />
                Duplicate
              </Button>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Setup checklist — hides itself once setup is complete or dismissed */}
      {!loading && (
        <EventSetupChecklist
          eventId={eventId!}
          status={setupStatus}
          canEdit={canEdit}
          canProvision={canProvision}
          dismissible
          hideWhenComplete
        />
      )}

      {/* Tab Navigation */}
      <div className="border-b">
        <nav className="flex gap-1 overflow-x-auto pb-px" role="tablist">
          {configTabs.map((tab) => {
            const Icon = tab.icon
            const isActive = currentTab === tab.id
            return (
              <button
                key={tab.id}
                role="tab"
                aria-selected={isActive}
                onClick={() => handleTabChange(tab.id)}
                className={cn(
                  'inline-flex items-center gap-2 whitespace-nowrap px-4 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px',
                  isActive
                    ? 'border-primary text-primary'
                    : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            )
          })}
        </nav>
      </div>

      {/* Tab Content — no wrapping Card */}
      <div>
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-center">
              <Loader2 className="mx-auto h-6 w-6 animate-spin text-muted-foreground" />
              <p className="mt-2 text-sm text-muted-foreground">Loading...</p>
            </div>
          </div>
        ) : (
          children || <Outlet />
        )}
      </div>

      <DuplicateEventDialog
        open={duplicateOpen}
        onOpenChange={setDuplicateOpen}
        event={event}
      />
    </div>
  )
}
